import React from "react";
import LegalLayout from "@/components/legal/LegalLayout";

const SECTIONS = [
  { id: "introduction", label: "1. Introduction" },
  { id: "definitions", label: "2. Definitions" },
  { id: "accounts", label: "3. Account & eligibility" },
  { id: "service", label: "4. The service" },
  { id: "informational", label: "5. Informational use only" },
  { id: "fees", label: "6. Fees & billing" },
  { id: "renewal", label: "7. Renewal & cancellation" },
  { id: "acceptable-use", label: "8. Acceptable use" },
  { id: "content", label: "9. Your content" },
  { id: "ip", label: "10. Intellectual property" },
  { id: "third-party", label: "11. Third-party services" },
  { id: "warranty", label: "12. Warranty disclaimer" },
  { id: "liability", label: "13. Limitation of liability" },
  { id: "indemnity", label: "14. Indemnity" },
  { id: "termination", label: "15. Suspension & termination" },
  { id: "modifications", label: "16. Changes to the service" },
  { id: "governing-law", label: "17. Governing law" },
  { id: "general", label: "18. General" },
  { id: "contact", label: "19. Contact" },
];

export default function TermsOfService() {
  return (
    <LegalLayout
      title="Terms of Service"
      subtitle="The terms governing your use of AgentFlow Compliance. Please read them carefully — by signing up or paying, you agree to be bound by them."
      lastUpdated="May 18, 2026"
      sections={SECTIONS}
    >
      <section id="introduction">
        <h2>1. Introduction</h2>
        <p>
          These Terms of Service (the <strong>"Terms"</strong>) are a binding
          agreement between you (<strong>"you"</strong> or the
          <strong> "Customer"</strong>) and{" "}
          <strong>AgentFlow Ventures Ltd.</strong>, a company incorporated in
          the Cayman Islands (<strong>"AgentFlow"</strong>,{" "}
          <strong>"we"</strong>, <strong>"us"</strong> or
          <strong> "our"</strong>). They govern your access to and use of the
          AgentFlow Compliance website, applications and related services
          (collectively, the <strong>"Service"</strong>).
        </p>
        <p>
          By creating an account, accessing the Service, or paying for a
          subscription you agree to these Terms, our{" "}
          <a href="/privacy">Privacy Policy</a> and our{" "}
          <a href="/refund">Refund Policy</a>. If you do not agree, do not use
          the Service.
        </p>
      </section>

      <section id="definitions">
        <h2>2. Definitions</h2>
        <ul>
          <li><strong>Account</strong>: the credentials and workspace associated with you on the Service.</li>
          <li><strong>Customer Content</strong>: documents, text and other materials you upload or generate using the Service.</li>
          <li><strong>Subscription</strong>: any paid plan, monthly or annual, including Pro and Business tiers.</li>
          <li><strong>Workspace</strong>: a logical container for your team, documents, scans and reports inside the Service.</li>
        </ul>
      </section>

      <section id="accounts">
        <h2>3. Account & eligibility</h2>
        <p>
          You must be at least 18 years old and able to form a binding contract
          to use the Service. You agree to provide accurate registration
          information and to keep your password confidential. You are
          responsible for all activity that occurs under your account.
        </p>
        <p>
          You may add additional users to your Workspace. Each invited user
          must agree to these Terms before accessing the Service.
        </p>
      </section>

      <section id="service">
        <h2>4. The service</h2>
        <p>
          AgentFlow Compliance provides AI-assisted scanning of business
          documents (policies, contracts, terms, etc.) and generates risk
          summaries, recommended fixes and audit-ready reports. The features
          available depend on the Subscription plan you select. See our{" "}
          <a href="/pricing">Pricing page</a> for current plans.
        </p>
      </section>

      <section id="informational">
        <h2>5. Informational use only — not legal advice</h2>
        <p>
          <strong>
            The Service is an informational tool. It does not constitute legal
            advice and does not create an attorney–client relationship.
          </strong>{" "}
          Outputs are generated by automated systems and may contain errors,
          omissions or inaccuracies. You should not rely on the Service as a
          substitute for advice from a qualified legal professional in your
          jurisdiction. AgentFlow expressly disclaims any liability arising
          from your reliance on Service outputs.
        </p>
      </section>

      <section id="fees">
        <h2>6. Fees & billing</h2>
        <p>
          Paid plans are billed in advance on a monthly or annual cycle in U.S.
          dollars. Payment is processed by our third-party payment processor
          (currently <strong>Paddle.com</strong>, who acts as the merchant of
          record). By subscribing you authorise the processor to charge your
          payment method on each renewal date until cancellation.
        </p>
        <p>
          Applicable taxes, including VAT, GST and sales tax, are calculated
          and remitted by the payment processor where required by law.
        </p>
      </section>

      <section id="renewal">
        <h2>7. Renewal & cancellation</h2>
        <p>
          Subscriptions automatically renew at the end of each billing period
          at the then-current rate unless cancelled before the renewal date.
          You may cancel at any time from your billing settings; cancellation
          takes effect at the end of the current paid period and you retain
          access through that period.
        </p>
        <p>
          Refunds are governed by our <a href="/refund">Refund Policy</a>.
        </p>
      </section>

      <section id="acceptable-use">
        <h2>8. Acceptable use</h2>
        <p>You agree not to use the Service to:</p>
        <ul>
          <li>upload content you do not own or have authorisation to upload;</li>
          <li>upload malicious code, viruses or content that infringes intellectual property;</li>
          <li>attempt to reverse-engineer, decompile or circumvent the Service;</li>
          <li>resell or sublicense the Service without our prior written consent;</li>
          <li>use the Service to harass, defame, defraud or harm another party;</li>
          <li>use the Service to violate any applicable law or regulation.</li>
        </ul>
      </section>

      <section id="content">
        <h2>9. Your content</h2>
        <p>
          You retain all rights in Customer Content. You grant AgentFlow a
          limited, non-exclusive, worldwide licence to host, process and
          analyse Customer Content solely to provide and improve the Service
          for you. We do not train shared foundation models on your content.
        </p>
        <p>
          You are responsible for the lawfulness of Customer Content and for
          obtaining any consents required from data subjects.
        </p>
      </section>

      <section id="ip">
        <h2>10. Intellectual property</h2>
        <p>
          The Service, including all software, branding, design, text, graphics
          and trademarks, is owned by AgentFlow or its licensors and is
          protected by intellectual property laws. Nothing in these Terms
          transfers any of those rights to you, except the limited right to
          access and use the Service in accordance with these Terms.
        </p>
      </section>

      <section id="third-party">
        <h2>11. Third-party services</h2>
        <p>
          The Service relies on third-party providers including (without
          limitation) cloud hosting, AI model providers, email delivery and
          payment processing. Use of those providers is subject to their
          respective terms. AgentFlow is not responsible for the acts or
          omissions of any third-party provider.
        </p>
      </section>

      <section id="warranty">
        <h2>12. Warranty disclaimer</h2>
        <p>
          <strong>
            The Service is provided on an "as is" and "as available" basis
            without warranties of any kind, express or implied.
          </strong>{" "}
          We do not warrant that the Service will be uninterrupted, error-free,
          accurate, or fit for any particular purpose. To the maximum extent
          permitted by law, we disclaim all implied warranties of
          merchantability, fitness for a particular purpose and
          non-infringement.
        </p>
      </section>

      <section id="liability">
        <h2>13. Limitation of liability</h2>
        <p>
          To the maximum extent permitted by law, AgentFlow's aggregate
          liability arising out of or relating to the Service, whether in
          contract, tort or otherwise, will not exceed the greater of (a) the
          fees you paid to us in the twelve (12) months preceding the event
          giving rise to the claim, or (b) one hundred U.S. dollars (US$100).
        </p>
        <p>
          We will not be liable for any indirect, incidental, special,
          consequential or punitive damages, including loss of profits,
          revenue, data, goodwill or business opportunity.
        </p>
      </section>

      <section id="indemnity">
        <h2>14. Indemnity</h2>
        <p>
          You will indemnify, defend and hold harmless AgentFlow and its
          officers, directors, employees and agents from and against any
          claims, damages, liabilities and reasonable expenses (including
          legal fees) arising from your breach of these Terms, your Customer
          Content, or your violation of any law or third-party right.
        </p>
      </section>

      <section id="termination">
        <h2>15. Suspension & termination</h2>
        <p>
          We may suspend or terminate your access to the Service at any time
          if you materially breach these Terms, fail to pay fees when due, or
          use the Service in a way that creates risk for AgentFlow or other
          customers. You may terminate your account at any time by cancelling
          your subscription and ceasing to use the Service.
        </p>
        <p>
          Sections that by their nature should survive termination (including
          sections 9, 10, 12, 13, 14 and 17) will survive.
        </p>
      </section>

      <section id="modifications">
        <h2>16. Changes to the service & these Terms</h2>
        <p>
          We may modify the Service or these Terms from time to time. Material
          changes will be notified by email or in-product notice at least
          fourteen (14) days before they take effect, except where a shorter
          period is required by law or for security reasons. Your continued
          use of the Service after the effective date constitutes acceptance
          of the updated Terms.
        </p>
      </section>

      <section id="governing-law">
        <h2>17. Governing law & disputes</h2>
        <p>
          These Terms are governed by the laws of the <strong>Cayman
          Islands</strong>, without regard to its conflict-of-law principles.
          The courts of the Cayman Islands will have exclusive jurisdiction
          over any dispute, except that we may seek injunctive relief in any
          court of competent jurisdiction to protect our intellectual property.
        </p>
      </section>

      <section id="general">
        <h2>18. General</h2>
        <ul>
          <li><strong>Entire agreement.</strong> These Terms, the Privacy Policy and the Refund Policy constitute the entire agreement between you and us regarding the Service.</li>
          <li><strong>Severability.</strong> If any provision is held invalid, the remaining provisions remain in force.</li>
          <li><strong>Assignment.</strong> You may not assign these Terms without our written consent. We may assign these Terms in connection with a merger, acquisition or sale of assets.</li>
          <li><strong>Force majeure.</strong> Neither party will be liable for delay or failure caused by events beyond reasonable control.</li>
          <li><strong>No waiver.</strong> A failure to enforce a provision does not waive our right to enforce it later.</li>
        </ul>
      </section>

      <section id="contact">
        <h2>19. Contact</h2>
        <p>
          Questions about these Terms? Email{" "}
          <a href="mailto:support@agentflow.ventures">support@agentflow.ventures</a>.
        </p>
        <p>
          AgentFlow Ventures Ltd.<br />
          Cayman Islands
        </p>
      </section>
    </LegalLayout>
  );
}
